import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import kuromoji from 'kuromoji';
import {
  collection, doc, addDoc, deleteDoc, updateDoc, onSnapshot, writeBatch, getDoc, setDoc,
} from 'firebase/firestore';
import { db, logEvent } from '../firebase/config';
import { useAuth } from './AuthContext';
import type { Article, SavedWord, Deck, DictEntry, Token, ArticleQuota } from '../types';
import { toHiragana, INFLECTING_POS } from '../utils/japanese';

interface AppContextType {
  articles: Article[];
  decks: Deck[];
  savedWords: SavedWord[];
  dictLoading: boolean;
  dictError: string | null;
  lastUsedDeckId: string | null;
  articleQuota: ArticleQuota;
  parseArticle: (title: string, content: string) => Promise<Article | null>;
  addDeck: (name: string) => Promise<Deck>;
  deleteDeck: (deckId: string) => Promise<void>;
  renameDeck: (deckId: string, name: string) => Promise<void>;
  saveWord: (token: Token, deckId: string, articleTokens?: Token[], tokenIndex?: number) => Promise<void>;
  saveBatchWords: (tokens: Token[], deckId: string, articleTokens?: Token[]) => Promise<void>;
  removeWord: (wordId: string) => Promise<void>;
  deleteArticle: (articleId: string) => Promise<void>;
  updateWordSRS: (wordId: string, updates: Partial<Pick<SavedWord, 'interval' | 'ease' | 'dueAt' | 'reviewCount'>>) => Promise<void>;
  updateDeckLastReviewed: (deckId: string) => Promise<void>;
}

const AppContext = createContext<AppContextType | null>(null);

export function useAppContext() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be used within AppProvider');
  return ctx;
}

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function extractSentence(tokens: Token[], tokenIndex: number): string {
  // Find sentence boundaries (newlines or sentence-ending punctuation)
  const isBoundary = (t: Token) => t.surface === '\n' || /^[。！？!?]$/.test(t.surface);

  let start = tokenIndex;
  while (start > 0 && !isBoundary(tokens[start - 1])) start--;

  let end = tokenIndex;
  while (end < tokens.length - 1 && !isBoundary(tokens[end])) end++;

  return tokens.slice(start, end + 1)
    .filter(t => t.surface !== '\n')
    .map(t => t.surface)
    .join('');
}

export function AppProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const uid = user?.uid;

  const [articles, setArticles] = useState<Article[]>([]);
  const [decks, setDecks] = useState<Deck[]>([]);
  const [savedWords, setSavedWords] = useState<SavedWord[]>([]);
  const [dictLoading, setDictLoading] = useState(false);
  const [dictError, setDictError] = useState<string | null>(null);
  const [lastUsedDeckId, setLastUsedDeckId] = useState<string | null>(null);
  const [articleQuota, setArticleQuota] = useState<ArticleQuota>({ month: getCurrentMonth(), count: 0 });

  const tokenizerRef = useRef<kuromoji.Tokenizer<kuromoji.IpadicFeatures> | null>(null);
  const tokenizerPromiseRef = useRef<Promise<kuromoji.Tokenizer<kuromoji.IpadicFeatures>> | null>(null);
  const dictMapRef = useRef<Map<string, DictEntry> | null>(null);
  const dictPromiseRef = useRef<Promise<Map<string, DictEntry>> | null>(null);

  // Lazy-load kuromoji tokenizer on first use (not on mount)
  const getTokenizer = useCallback((): Promise<kuromoji.Tokenizer<kuromoji.IpadicFeatures>> => {
    if (tokenizerRef.current) return Promise.resolve(tokenizerRef.current);
    if (tokenizerPromiseRef.current) return tokenizerPromiseRef.current;
    tokenizerPromiseRef.current = new Promise((resolve, reject) => {
      kuromoji.builder({ dicPath: '/kuromoji-dict' }).build((err, tokenizer) => {
        if (err) {
          tokenizerPromiseRef.current = null;
          reject(err);
        } else {
          tokenizerRef.current = tokenizer;
          resolve(tokenizer);
        }
      });
    });
    return tokenizerPromiseRef.current;
  }, []);

  // Firestore real-time listeners
  useEffect(() => {
    if (!uid) {
      setArticles([]);
      setDecks([]);
      setSavedWords([]);
      setArticleQuota({ month: getCurrentMonth(), count: 0 });
      return;
    }
    const unsubArticles = onSnapshot(
      collection(db, 'users', uid, 'articles'),
      (snap) => setArticles(snap.docs.map(d => ({ id: d.id, ...d.data() } as Article))),
    );
    const unsubDecks = onSnapshot(
      collection(db, 'users', uid, 'decks'),
      (snap) => setDecks(snap.docs.map(d => ({ id: d.id, ...d.data() } as Deck))),
    );
    const unsubWords = onSnapshot(
      collection(db, 'users', uid, 'words'),
      (snap) => setSavedWords(snap.docs.map(d => ({ id: d.id, ...d.data() } as SavedWord))),
    );
    // Listen to article quota
    const unsubQuota = onSnapshot(
      doc(db, 'users', uid, 'meta', 'quota'),
      (snap) => {
        const data = snap.data() as ArticleQuota | undefined;
        const currentMonth = getCurrentMonth();
        if (data && data.month === currentMonth) {
          setArticleQuota(data);
        } else {
          setArticleQuota({ month: currentMonth, count: 0 });
        }
      },
    );
    return () => { unsubArticles(); unsubDecks(); unsubWords(); unsubQuota(); };
  }, [uid]);

  const getDictionary = useCallback((): Promise<Map<string, DictEntry>> => {
    if (dictMapRef.current) return Promise.resolve(dictMapRef.current);
    if (dictPromiseRef.current) return dictPromiseRef.current;

    const DICT_DB = 'jitendex-cache';
    const DICT_STORE = 'dict';
    const DICT_KEY = 'v1';

    const buildMap = (data: Record<string, { r: string; g: string }>): Map<string, DictEntry> => {
      const map = new Map<string, DictEntry>();
      for (const [term, entry] of Object.entries(data)) {
        map.set(term, { reading: entry.r, translation: entry.g });
      }
      dictMapRef.current = map;
      return map;
    };

    const readFromIDB = (): Promise<Record<string, { r: string; g: string }> | null> =>
      new Promise(resolve => {
        try {
          const req = indexedDB.open(DICT_DB, 1);
          req.onupgradeneeded = () => req.result.createObjectStore(DICT_STORE);
          req.onerror = () => resolve(null);
          req.onsuccess = () => {
            const tx = req.result.transaction(DICT_STORE, 'readonly');
            const get = tx.objectStore(DICT_STORE).get(DICT_KEY);
            get.onsuccess = () => resolve(get.result ?? null);
            get.onerror = () => resolve(null);
          };
        } catch { resolve(null); }
      });

    const writeToIDB = (data: Record<string, { r: string; g: string }>): void => {
      try {
        const req = indexedDB.open(DICT_DB, 1);
        req.onupgradeneeded = () => req.result.createObjectStore(DICT_STORE);
        req.onsuccess = () => {
          const tx = req.result.transaction(DICT_STORE, 'readwrite');
          tx.objectStore(DICT_STORE).put(data, DICT_KEY);
        };
      } catch { /* ignore write failures */ }
    };

    dictPromiseRef.current = readFromIDB()
      .then(cached => {
        if (cached) return buildMap(cached);
        return fetch('/jitendex.json')
          .then(res => {
            if (!res.ok) throw new Error('Dictionary not found. Run: node scripts/build-dictionary.js');
            return res.json() as Promise<Record<string, { r: string; g: string }>>;
          })
          .then(data => {
            writeToIDB(data);
            return buildMap(data);
          });
      })
      .catch(err => {
        dictPromiseRef.current = null;
        throw err;
      });
    return dictPromiseRef.current;
  }, []);

  const tokenizeLine = useCallback((tokenizer: kuromoji.Tokenizer<kuromoji.IpadicFeatures>, line: string, dict: Map<string, DictEntry>): Token[] => {
    type IpadicToken = kuromoji.IpadicFeatures;
    const raw: IpadicToken[] = tokenizer.tokenize(line);
    const groups: IpadicToken[][] = [];
    for (const t of raw) {
      const prev = groups[groups.length - 1];
      if (!prev) { groups.push([t]); continue; }

      const head = prev[0];
      const headPos = head.pos;
      const isVerbHead = headPos === '動詞' || headPos === '形容詞';

      const isAux = t.pos === '助動詞' || (t.pos === '動詞' && t.pos_detail_1 === '接尾');
      if (isVerbHead && isAux) { prev.push(t); continue; }

      if (isVerbHead && t.pos === '助詞' && t.pos_detail_1 === '接続助詞' && /^[てで]$/.test(t.surface_form)) {
        prev.push(t); continue;
      }

      const isDependentVerb = t.pos === '動詞' && t.pos_detail_1 === '非自立';
      if (isVerbHead && isDependentVerb) { prev.push(t); continue; }

      if (headPos === '名詞' && t.pos === '名詞' && t.pos_detail_1 === '接尾') { prev.push(t); continue; }

      if (t.surface_form === '的') { prev.push(t); continue; }

      groups.push([t]);
    }
    return groups.map(group => {
      const head = group[0];
      const surface = group.map(t => t.surface_form).join('');
      const basicForm = head.basic_form && head.basic_form !== '*' ? head.basic_form : head.surface_form;
      const skipLookup = head.pos === '記号';
      let entry: DictEntry | undefined;
      if (!skipLookup) {
        entry = INFLECTING_POS.has(head.pos)
          ? (dict.get(basicForm) ?? dict.get(surface))
          : (dict.get(surface) ?? dict.get(basicForm));
      }
      const groupReading = head.reading ? toHiragana(group.map(t => t.reading || t.surface_form).join('')) : null;
      const reading = groupReading ?? entry?.reading ?? surface;
      return { surface, basicForm, reading, translation: entry?.translation ?? '' };
    });
  }, []);

  const parseArticle = useCallback(async (title: string, content: string): Promise<Article | null> => {
    if (!uid || !content.trim() || content.length > 10000) return null;

    setDictLoading(true);
    setDictError(null);
    let tokenizer: kuromoji.Tokenizer<kuromoji.IpadicFeatures>;
    let dict: Map<string, DictEntry>;
    try {
      [tokenizer, dict] = await Promise.all([getTokenizer(), getDictionary()]);
    } catch (err) {
      setDictError(err instanceof Error ? err.message : 'Failed to load tokenizer or dictionary');
      setDictLoading(false);
      return null;
    }
    setDictLoading(false);

    const newlineToken: Token = { surface: '\n', basicForm: '\n', reading: '', translation: '' };
    const lines = content.split(/\r?\n/);
    const tokens: Token[] = [];

    for (let i = 0; i < lines.length; i++) {
      tokens.push(...tokenizeLine(tokenizer, lines[i], dict));
      if (i < lines.length - 1) tokens.push(newlineToken);
    }

    // Also tokenize the title
    const titleTokens = tokenizeLine(tokenizer, title || '無題', dict);

    const articleData = {
      title: title || '無題',
      content,
      tokens,
      titleTokens,
      savedAt: Date.now(),
    };
    const docRef = await addDoc(collection(db, 'users', uid, 'articles'), articleData);
    logEvent('article_parsed', { title_length: title.length, content_length: content.length, token_count: tokens.length });

    // Increment monthly article quota counter
    const currentMonth = getCurrentMonth();
    const quotaRef = doc(db, 'users', uid, 'meta', 'quota');
    const quotaSnap = await getDoc(quotaRef);
    const quotaData = quotaSnap.data() as ArticleQuota | undefined;
    if (quotaData && quotaData.month === currentMonth) {
      await setDoc(quotaRef, { month: currentMonth, count: quotaData.count + 1 });
    } else {
      await setDoc(quotaRef, { month: currentMonth, count: 1 });
    }

    return { id: docRef.id, ...articleData };
  }, [uid, getTokenizer, getDictionary, tokenizeLine]);

  const addDeck = useCallback(async (name: string): Promise<Deck> => {
    if (!uid) throw new Error('Not authenticated');
    const deckData = { name, createdAt: Date.now() };
    const docRef = await addDoc(collection(db, 'users', uid, 'decks'), deckData);
    logEvent('deck_created', { deck_name: name });
    return { id: docRef.id, ...deckData };
  }, [uid]);

  const deleteDeck = useCallback(async (deckId: string) => {
    if (!uid) return;
    // Delete all words in this deck
    const wordsInDeck = savedWords.filter(w => w.deckId === deckId);
    const batch = writeBatch(db);
    wordsInDeck.forEach(w => batch.delete(doc(db, 'users', uid, 'words', w.id)));
    batch.delete(doc(db, 'users', uid, 'decks', deckId));
    await batch.commit();
  }, [uid, savedWords]);

  const renameDeck = useCallback(async (deckId: string, name: string) => {
    if (!uid) return;
    await updateDoc(doc(db, 'users', uid, 'decks', deckId), { name });
  }, [uid]);

  const saveWord = useCallback(async (token: Token, deckId: string, articleTokens?: Token[], tokenIndex?: number) => {
    if (!uid || !token.translation) return;
    const textToSave = token.basicForm || token.surface;
    const exists = savedWords.some(w => w.text === textToSave && w.deckId === deckId);
    if (exists) return;

    const now = Date.now();
    let exampleSentence: string | undefined;
    if (articleTokens && tokenIndex !== undefined && tokenIndex >= 0) {
      exampleSentence = extractSentence(articleTokens, tokenIndex);
    }

    await addDoc(collection(db, 'users', uid, 'words'), {
      text: textToSave,
      reading: token.reading,
      translation: token.translation,
      exampleSentence: exampleSentence || '',
      addedAt: now,
      deckId,
      interval: 0,
      ease: 2.5,
      dueAt: now,
      reviewCount: 0,
    });
    logEvent('word_saved', { word: textToSave });
    setLastUsedDeckId(deckId);
  }, [uid, savedWords]);

  const saveBatchWords = useCallback(async (tokens: Token[], deckId: string, articleTokens?: Token[]) => {
    if (!uid) return;
    const seen = new Set<string>();
    // Also track existing words in this deck
    const existingTexts = new Set(savedWords.filter(w => w.deckId === deckId).map(w => w.text));
    const now = Date.now();

    const batch = writeBatch(db);
    let count = 0;
    for (const token of tokens) {
      if (!token.translation) continue;
      const textToSave = token.basicForm || token.surface;
      if (seen.has(textToSave) || existingTexts.has(textToSave)) continue;
      seen.add(textToSave);

      let exampleSentence = '';
      if (articleTokens) {
        const idx = articleTokens.findIndex(t => t.surface === token.surface);
        if (idx >= 0) exampleSentence = extractSentence(articleTokens, idx);
      }

      const wordRef = doc(collection(db, 'users', uid, 'words'));
      batch.set(wordRef, {
        text: textToSave,
        reading: token.reading,
        translation: token.translation,
        exampleSentence,
        addedAt: now,
        deckId,
        interval: 0,
        ease: 2.5,
        dueAt: now,
        reviewCount: 0,
      });
      count++;
      // Firestore batches limited to 500
      if (count >= 450) break;
    }
    if (count > 0) {
      await batch.commit();
      logEvent('batch_words_saved', { count });
      setLastUsedDeckId(deckId);
    }
  }, [uid, savedWords]);

  const removeWord = useCallback(async (wordId: string) => {
    if (!uid) return;
    await deleteDoc(doc(db, 'users', uid, 'words', wordId));
  }, [uid]);

  const deleteArticle = useCallback(async (articleId: string) => {
    if (!uid) return;
    await deleteDoc(doc(db, 'users', uid, 'articles', articleId));
    // Note: we intentionally do NOT decrement the monthly quota counter
  }, [uid]);

  const updateWordSRS = useCallback(async (wordId: string, updates: Partial<Pick<SavedWord, 'interval' | 'ease' | 'dueAt' | 'reviewCount'>>) => {
    if (!uid) return;
    await updateDoc(doc(db, 'users', uid, 'words', wordId), updates);
  }, [uid]);

  const updateDeckLastReviewed = useCallback(async (deckId: string) => {
    if (!uid) return;
    await updateDoc(doc(db, 'users', uid, 'decks', deckId), { lastReviewedAt: Date.now() });
  }, [uid]);

  return (
    <AppContext.Provider value={{
      articles, decks, savedWords,
      dictLoading, dictError,
      lastUsedDeckId, articleQuota,
      parseArticle, addDeck, deleteDeck, renameDeck,
      saveWord, saveBatchWords, removeWord, deleteArticle,
      updateWordSRS, updateDeckLastReviewed,
    }}>
      {children}
    </AppContext.Provider>
  );
}
